import { useEffect } from 'react';

/** Lock body scroll while a modal/drawer is open. Without this, the
 *  page underneath the drawer can still be scrolled (and on macOS you
 *  get a second scrollbar visible to the left of the drawer's).
 *
 *  Body-level overflow:hidden handles routes where document scroll is
 *  the page scroll. Many routes (e.g. /home) use an inner flex column
 *  with its own overflow-y-auto child, in which case body overflow is
 *  already hidden — so this hook *also* freezes any element marked
 *  with `data-scroll-lock-target`. Pages that own a non-body scroll
 *  region tag that element; the hook stashes + restores their
 *  overflow value too.
 *
 *  Refcount on `data-scroll-locks` so nested locks (drawer over
 *  drawer) restore correctly only when the outermost one closes. */
export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    const body = document.body;
    const count = Number(body.dataset['scrollLocks'] ?? '0');
    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-scroll-lock-target]'));
    if (count === 0) {
      // First lock — stash the original overflow + paddingRight so the
      // page doesn't jump when the scrollbar disappears.
      body.dataset['prevOverflow'] = body.style.overflow;
      body.dataset['prevPaddingRight'] = body.style.paddingRight;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
      // Stash + lock each tagged page-scroll region.
      for (const el of targets) {
        el.dataset['prevOverflow'] = el.style.overflow;
        el.style.overflow = 'hidden';
      }
    }
    body.dataset['scrollLocks'] = String(count + 1);
    return () => {
      const next = Number(body.dataset['scrollLocks'] ?? '1') - 1;
      if (next <= 0) {
        body.style.overflow = body.dataset['prevOverflow'] ?? '';
        body.style.paddingRight = body.dataset['prevPaddingRight'] ?? '';
        delete body.dataset['scrollLocks'];
        delete body.dataset['prevOverflow'];
        delete body.dataset['prevPaddingRight'];
        for (const el of targets) {
          el.style.overflow = el.dataset['prevOverflow'] ?? '';
          delete el.dataset['prevOverflow'];
        }
      } else {
        body.dataset['scrollLocks'] = String(next);
      }
    };
  }, [locked]);
}
